import { BolDataV3 } from '../types/bolTypes';
import { generateBOLHTML } from '../BillOfLading';
import { BOL3_STYLES } from './bolBaseStyles';
import { BOL3_PRINT_MEDIA_STYLES } from './bolPrintStyles';
import { BOL3_SAFARI_PRINT_STYLES } from './bolSafariPrintStyles';
import { BOL3_FIREFOX_BORDER_STYLES } from './bolFirefoxPrintStyles';

/**
 * 检测是否为 Safari 浏览器
 */
const isSafari = (): boolean => {
    const ua = navigator.userAgent.toLowerCase();
    return /safari/.test(ua) && !/chrome/.test(ua) && !/edg/.test(ua);
};

/**
 * 检测是否为 Firefox（UA 不含 safari/chrome，不会与上面的判断冲突）
 */
const isFirefox = (): boolean => /firefox/.test(navigator.userAgent.toLowerCase());

/**
 * 根据浏览器类型获取打印样式
 * - Safari：独立分支，@page 与重排 hack 都不同
 * - Firefox：复用 Chrome 分支，仅叠加边框覆盖修正预览缺线（见 bolFirefoxPrintStyles.ts）
 * - Chrome / Edge：同为 Blink，几何输出一致，共用一条分支
 */
const getPrintStyles = (): string => {
    if (isSafari()) {
        return `${BOL3_STYLES}${BOL3_SAFARI_PRINT_STYLES}`;
    }
    if (isFirefox()) {
        return `${BOL3_STYLES}${BOL3_PRINT_MEDIA_STYLES}${BOL3_FIREFOX_BORDER_STYLES}`;
    }
    return `${BOL3_STYLES}${BOL3_PRINT_MEDIA_STYLES}`;
};

/**
 * 打印容器和样式元素的 ID（与 v1/v2 区分开，互不干扰）
 */
const PRINT_CONTAINER_ID = 'bol3-print-container';
const PRINT_STYLE_ID = 'bol3-print-style';

/**
 * 清理打印相关的 DOM 元素
 */
const cleanupPrintElements = (): void => {
    document.getElementById(PRINT_CONTAINER_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();
};

/**
 * 处理打印完成后的清理
 */
const handlePrintCleanup = (): void => {
    window.addEventListener('afterprint', cleanupPrintElements, { once: true });
    // 备用清理（如果 afterprint 不触发）
    setTimeout(cleanupPrintElements, 2000);
};

/**
 * 打印 BOL v3（装车明细已合并在单据内，单页打印）
 */
export const printBol = (bolDataList: BolDataV3[]): void => {
    cleanupPrintElements();
    const safari = isSafari();

    const printContent = bolDataList.map((bolData, index) => {
        const html = generateBOLHTML(bolData);
        if (index < bolDataList.length - 1) {
            return html + '<div class="bol3-page-break"></div>';
        }
        return html;
    }).join('');

    const printContainer = document.createElement('div');
    printContainer.id = PRINT_CONTAINER_ID;

    const styleElement = document.createElement('style');
    styleElement.id = PRINT_STYLE_ID;
    styleElement.textContent = getPrintStyles();

    printContainer.innerHTML = printContent;
    document.body.appendChild(printContainer);
    document.head.appendChild(styleElement);

    // Safari 需要强制重排以确保样式应用
    if (safari) {
        void printContainer.offsetHeight;
        setTimeout(() => {
            window.print();
            handlePrintCleanup();
        }, 100);
    } else {
        window.print();
        handlePrintCleanup();
    }
};
